import { createContext, useEffect, useState } from "react";
import { Profiles } from "components/Profiles";
import { profileServices, IProfileResponse } from "services/profile";
import { Typography } from "components/Typography";
import { userService } from "services/user";
import { getUserId } from "utils/getUserId";
import { getCartId } from "utils/getCartId";
import { createUserCardService, fetchCardItemsService } from "services/cart";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { Navbar } from "components/Navbar";
import { CalculatedItems } from "components/CalculatedItems/CalculatedItems";
import { ChooseProfile } from "components/ChooseProfile/ChooseProfile";
import { DownloadContainer } from "components/DownloadContainer/DownloadContainer";

const WrapperLogo = styled.div`
  margin-bottom: 0.5rem;
  @media (min-width: 800px) {
    margin-bottom: 1rem;
  }
`;

export interface CalculatorContextType {
  itemCount: number;
  handleItemCount: (type: number) => void;
  translations: {
    [key: string]: string;
  };
}

export const CalculatorContext = createContext<CalculatorContextType | null>(
  null
);

function App() {
  const [profiles, setProfiles] = useState<IProfileResponse[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [isOpenClearModalm, setIsOpenClearModal] = useState(false);
  const [removedItemId, setRemovedItemId] = useState("");
  const [calculatedItemsCount, setCalculatedItemsCount] = useState(0);
  const [userId, setUserId] = useState<string | null>(getUserId()); // Use state to track user ID
  const [itemCount, setItemCount] = useState<number>(0); 
  const [translations, setTranslations] = useState(null);

  const lang = "pl";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `https://calculator.fpg.com.pl/api/translations/${lang}`
        );
        const jsonData = await response.json();
        setTranslations(jsonData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleItemCount = (type: number) => {
    setItemCount(Number(type));
  };

  const cartId = getCartId();

  const fetchCardItems = async () => {
    const response = await fetchCardItemsService(getCartId());
    setCartItems(response.data.items);
  };

  const handleOpenConfirmModal = (id: string) => {
    setIsOpen(true);
    setRemovedItemId(id);
  };

  const handleOpenClearConfirmModal = (id: string) => {
    if (cartItems.length === 0) return;

    setIsOpenClearModal(true);
    setRemovedItemId(id);
  };

  const fetchProfile = () => {
    profileServices().then((res) => {
      if (res.status === 200) {
        setProfiles(res.data);
      }
    });
  };

  const createCard = async () => {
    if (cartId) return;
    if (userId) {
      const response = await createUserCardService(userId);
      if (response.status === 200) {
        localStorage.setItem("fpg_cartId", response.data.id);
      }
    }
  };

  const generateUUID = async () => {
    const uuid = uuidv4();
    localStorage.setItem("fpg_userId", uuid);
    await userService(uuid);
    setUserId(uuid);
  };

  useEffect(() => {
    // Check if the user ID is already generated, if not, generate it
    if (!userId) {
      generateUUID();
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (userId) {
      createCard();
      setCalculatedItemsCount(cartItems.length);
    }
  }, [cartItems, userId]);

  const [fullscreenView, setFullscreenView] = useState<
    "POZYCJE" | "WYBIERZ" | "POBIERZ" | null
  >(null);

  // Functions to toggle between the fullscreen views
  const showCalculatedItems = () => {
    setFullscreenView("POZYCJE");
  };

  const showChooseProfile = () => {
    setFullscreenView("WYBIERZ");
  };

  const showDownload = () => {
    setFullscreenView("POBIERZ");
  };

  useEffect(() => {
    // fetchCardItems();
    setCalculatedItemsCount(cartItems.length);
  }, [cartItems]);

  return (
    <>
      {translations && (
        <>
          <CalculatorContext.Provider value={{ itemCount, translations, handleItemCount }}>
            <main id="main-container">
              <WrapperLogo>
                <Typography variant="h1">{translations["header-text"]}</Typography>
                <Typography variant="h3">{translations["subheader-text"]}</Typography>
              </WrapperLogo>
              <Profiles profiles={profiles} />
            </main>
            <Navbar
              showCalculatedItems={showCalculatedItems}
              showChooseProfile={showChooseProfile}
              showDownload={showDownload}
              calculatedItemsCount={itemCount}
            />
            {fullscreenView === "POZYCJE" && (
              <CalculatedItems profiles={profiles} />
            )}
            {fullscreenView === "WYBIERZ" && <ChooseProfile />}
            {fullscreenView === "POBIERZ" && <DownloadContainer />}
          </CalculatorContext.Provider>
        </>
      )}
    </>
  );
}

export default App;
